import { atom } from '@reatom/framework'
import { action } from '@reatom/framework'
import { useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import styled from 'styled-components'

import { ApplinkURL, Deeplink } from '@root/entities/deeplinking'
import {
  openBuyBoostersFlowEvent,
  openBuyPremiumFlowEvent,
  openExtendPremiumFlowEvent,
} from '@root/entities/premium'
import { isTauriOrBrowserLocalAtom } from '@root/shared'

import { BuyBoostersDialog } from './buy-boosters-dialog'
import { BuyPremiumDialog } from './buy-premium-dialog'
import { ExtendPremiumDialog } from './extend-premium-dialog'

import type {
  CurrencyModel,
  CurrencyWithPremium,
} from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium'

const buyPremiumParamsAtom = atom<
  | {
      currency: CurrencyModel
      premium: PremiumMetaModel | undefined
      shouldCheckSafety?: boolean
    }
  | undefined
>(undefined, 'buyPremiumParamsAtom')

const buyBoostersParamsAtom = atom<
  CurrencyWithPremium.CurrencyWithPremium | undefined
>(undefined, 'buyBoostersParamsAtom')

const extendBoostersParamsAtom = atom<
  CurrencyWithPremium.CurrencyWithPremium | undefined
>(undefined, 'extendBoostersParamsAtom')

const openDeepLinkInApp = (currency: CurrencyModel) => {
  const deepLink = new Deeplink.CheckCurrency({
    network: currency.network,
    queryAddress: currency.address,
  })

  ApplinkURL.trigger(deepLink)
}

const openBuyBoostersFlowAction = action(
  (ctx, params: CurrencyWithPremium.CurrencyWithPremium) => {
    if (ctx.get(isTauriOrBrowserLocalAtom)) {
      buyBoostersParamsAtom(ctx, params)
    } else {
      openDeepLinkInApp(params.currency)
    }
  },
  'openBuyBoostersFlowAction',
)

const openBuyPremiumFlowAction = action(
  (
    ctx,
    params: {
      currency: CurrencyModel
      premium: PremiumMetaModel | undefined
    },
  ) => {
    if (ctx.get(isTauriOrBrowserLocalAtom)) {
      buyPremiumParamsAtom(ctx, params)
    } else {
      openDeepLinkInApp(params.currency)
    }
  },
  'openBuyPremiumFlowAction',
)

openBuyPremiumFlowEvent.onCall((ctx, _, [params]) => {
  openBuyPremiumFlowAction(ctx, params)
})

openBuyBoostersFlowEvent.onCall((ctx, _, [params]) => {
  if (ctx.get(params.premium.premium)) {
    openBuyBoostersFlowAction(ctx, {
      currency: params.currency,
      premium: params.premium,
    })
    return
  }
  openBuyPremiumFlowAction(ctx, params)
})

openExtendPremiumFlowEvent.onCall((ctx, _, [params]) => {
  if (ctx.get(params.premium.isAllowedToExtend)) {
    extendBoostersParamsAtom(ctx, params)
  }
})

export const PremiumBuyDialogs: React.FC = () => {
  const [boostersParams, setBoostersParams] = useAtom(buyBoostersParamsAtom)
  const [premiumParams, setPremiumParams] = useAtom(buyPremiumParamsAtom)
  const [extendBoostersParams, setExtendBoostersParams] = useAtom(
    extendBoostersParamsAtom,
  )
  return (
    <Root>
      <AnimatePresence>
        {premiumParams ? (
          <BuyPremiumDialog
            currency={premiumParams.currency}
            premium={premiumParams.premium}
            open
            shouldCheckSafety={premiumParams.shouldCheckSafety}
            onOpenChange={(open) => {
              if (!open) {
                setPremiumParams(undefined)
              }
            }}
          />
        ) : undefined}
      </AnimatePresence>
      <AnimatePresence>
        {boostersParams ? (
          <BuyBoostersDialog
            currency={boostersParams.currency}
            premium={boostersParams.premium}
            open
            onOpenChange={(open) => {
              if (!open) {
                setBoostersParams(undefined)
              }
            }}
          />
        ) : undefined}
      </AnimatePresence>
      <AnimatePresence>
        {extendBoostersParams?.premium ? (
          <ExtendPremiumDialog
            currency={extendBoostersParams.currency}
            premium={extendBoostersParams.premium}
            open
            onOpenChange={(open) => {
              if (!open) {
                setExtendBoostersParams(undefined)
              }
            }}
          />
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Root = styled.div``
